import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { AlertCircleIcon } from 'lucide-react-native';
import { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Input, InputField } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { SMS_NOTIFICATION_COPY, SMS_NOTIFICATION_REASSURANCE } from '@/constants/smsPromptCopy';
import { useMaskedController } from '@/hooks/useMaskedController';
import { useToaster } from '@/hooks/useToaster';
import { clearUserAttributesCache } from '@/services/auth';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { userIdAtom } from '@/services/globalState';
import { setNotificationPreferenceV2 } from '@/services/setNotificationPreferenceV2';
import { updateUser } from '@/services/updateUser';
import { segmentClient } from '@/util/analytics';
import {
  type SmsNotificationEvent,
  trackMobileNumberSetupDismissed,
  trackMobileNumberSetupFailed,
  trackMobileNumberSetupViewed,
  trackSmsNotificationsEnabled,
} from '@/util/analyticsEvents';
import { queryClient } from '@/util/queryClient';

export type MobileNumberSetupSource =
  | 'reference_wizard'
  | 'first_message'
  | 'notification_settings'
  | 'inquiry_success';

/** @deprecated Use `MobileNumberSetupSource`. Kept as an alias while
 * existing imports migrate. */
export type ExpertMobileNumberSetupSource = MobileNumberSetupSource;

const phoneNumberSchema = z.object({
  mobilePhone: z
    // The input is masked to digits-only, so the only reachable failure is
    // wrong length — one combined rule keeps the message correct for empty,
    // partial, and complete states (a separate "only digits" rule would show
    // misleadingly on an empty field).
    .string()
    .regex(/^\d{10}$/, 'Enter a 10-digit mobile number'),
});

type PhoneFormData = z.infer<typeof phoneNumberSchema>;

interface MobileNumberSetupFormProps {
  source: MobileNumberSetupSource;
  /** Called after the mobile number is saved successfully. */
  onComplete?: () => void;
  /** Called when the user skips the prompt. */
  onSkip?: () => void;
  /** When provided, render a third "I don't want text notifications"
   * action (the submitter opt-out). Omit on surfaces with no opt-out. */
  onOptOut?: () => void;
  /** Render the heading + context copy above the field. Hosts that
   * already provide their own chrome (e.g. a modal header) can omit. */
  showHeader?: boolean;
  /** Autofocus the phone input on mount. Use when the form replaces
   * other content in place (e.g. the reference wizard's inline swap) so
   * keyboard / screen-reader focus lands in the new content instead of
   * being stranded on a now-unmounted control. */
  autoFocus?: boolean;
  /** Also enable the `newMessage` SMS preference when saving the number.
   * The submitter post-inquiry flow opts the user into SMS as part of
   * the same action; expert surfaces only capture the number. */
  enableSmsOnSave?: boolean;
  /** Primary button label. Defaults to "Save". The submitter flow uses
   * a conversion-tuned "Turn on text notifications". */
  saveLabel?: string;
  /** Pre-fill the field with an existing number (10 digits, no +1).
   * Used by the submitter flow, which can fire when the user already
   * has a number on file but hasn't enabled the SMS preference. */
  defaultMobilePhone?: string;
  /** Require the field to change before the primary button enables.
   * Defaults to true (expert flows capture a NEW number). The submitter
   * flow sets false so a pre-filled, valid number can be submitted as-is
   * to just turn on SMS. */
  requireDirty?: boolean;
  /** Show the built-in success toast on save. Defaults to true. Hosts that
   * surface their own, more contextual confirmation (e.g. the reference
   * wizard's "We'll let you know when your references are ready") set false
   * to avoid stacking two success toasts. */
  showSuccessToast?: boolean;
}

/**
 * The SMS notifications a surface's opt-in turns on. Expert surfaces enable
 * the full expert set, so "Turn on texts" lives up to its label rather than
 * flipping only the one event that triggered the prompt; the submitter
 * post-inquiry surface only has new-message texts (submitters don't receive
 * inquiries or request references). `notification_settings` captures the
 * number only (`enableSmsOnSave` is false there), so its list is never used.
 * Only events currently off are enabled on save (see `onSubmit`).
 */
const SMS_EVENTS_BY_SOURCE: Record<MobileNumberSetupSource, SmsNotificationEvent[]> = {
  reference_wizard: ['newInquiry', 'newMessage', 'referencesReady'],
  first_message: ['newInquiry', 'newMessage', 'referencesReady'],
  inquiry_success: ['newMessage'],
  notification_settings: [],
};

/**
 * Phone-number capture form for enabling SMS notifications. Renders as
 * plain inline content (no overlay chrome) so it can be embedded either
 * inside a modal (`ExpertMobileNumberSetupModal`, `SMSNotificationModal`)
 * or directly inside another surface's flow — e.g. as a step in the
 * reference-request wizard — without stacking a second modal on top of
 * an existing one.
 *
 * Single source of truth for the phone field across every SMS prompt:
 * expert (reference wizard, first message, notification settings) and
 * submitter (post-inquiry). Per the components rule "Reuse before you
 * build", surfaces compose this rather than re-implementing the form.
 *
 * Owns its own form state, validation, and save mutation, and emits
 * Segment events: shown on mount, PROFILE_EDIT_SAVED on save, and
 * dismissed on every exit (Skip, or any other teardown — X, backdrop,
 * navigation — via the unmount guard).
 *
 * Uses the same validation and masking patterns as contact-details.tsx.
 */
export function MobileNumberSetupForm({
  source,
  onComplete,
  onSkip,
  onOptOut,
  showHeader = true,
  autoFocus = false,
  enableSmsOnSave = false,
  saveLabel,
  defaultMobilePhone = '',
  requireDirty = true,
  showSuccessToast = true,
}: MobileNumberSetupFormProps) {
  /**
   * Label the action by its effect: surfaces that enable SMS on save say
   * "Turn on texts" (even with no number yet — saving still turns it on),
   * while number-only capture (notification settings) says "Save". A caller
   * `saveLabel` overrides both.
   */
  const effectiveSaveLabel = saveLabel ?? (enableSmsOnSave ? 'Turn on texts' : 'Save');

  const smsEvents = SMS_EVENTS_BY_SOURCE[source];

  const userId = useAtomValue(userIdAtom);

  const toast = useToaster();

  const userQueryOptions = getCurrentUserV2QueryOptions(userId);

  const userQueryKey = userQueryOptions.queryKey;

  const { data: userData } = useQuery(userQueryOptions);

  /** SMS preference keys (e.g. `newMessageSms`) already set true — skip
   * re-enabling these so saving only turns on the events that are off. */
  const enabledSmsPreferences = userData?.notificationsPreferences as
    | Record<string, boolean>
    | undefined;

  /** True once the prompt has been explicitly resolved (saved or
   * skipped). Gates the unmount-dismissal event so an explicit Skip /
   * successful Save isn't double-counted as an implicit dismissal. */
  const resolvedRef = useRef(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors, isSubmitting, isDirty, isSubmitted },
  } = useForm<PhoneFormData>({
    defaultValues: {
      mobilePhone: defaultMobilePhone,
    },
    resolver: zodResolver(phoneNumberSchema),
    // Submit-gated: validate only when the primary button is pressed, then
    // re-validate live as they fix it. No error on blur or mid-typing.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const mobileNumber = useMaskedController({
    control,
    type: 'mobilePhone',
    setFormValue: (value) => {
      setValue('mobilePhone', value);

      trigger('mobilePhone');
    },
  });

  const { mutateAsync: updateUserMutation } = useMutation({
    mutationFn: updateUser,
  });

  const { mutateAsync: enableSmsPreferenceMutation } = useMutation({
    mutationFn: setNotificationPreferenceV2,
  });

  useEffect(() => {
    trackMobileNumberSetupViewed(source);

    // Fire once on mount — the form only mounts when the prompt is shown.
    // `source` is a stable prop, so the empty dep list is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Track every exit that isn't an explicit Save/Skip — X close, backdrop
   * tap, Escape, navigation, or the host unmounting the form (e.g. the
   * wizard revealing review). Without the unmount guard, only the Skip
   * button reports a dismissal and those outcomes go untracked. `source`
   * is stable, so the empty dep list is intentional.
   */
  useEffect(() => {
    return () => {
      if (resolvedRef.current) {
        return;
      }

      trackMobileNumberSetupDismissed({ source, dismissMethod: 'implicit' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = handleSubmit(async (data) => {
    try {
      // Captured before the save (cache still holds the pre-save value) so the
      // opt-in event can distinguish a brand-new number from a user who already
      // had one on file and is only flipping the SMS preference on.
      const hadPhoneNumber = Boolean(userData?.mobilePhone);

      segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
        source,
        editType: 'mobile_number',
      });

      // Turn on SMS for this surface's events that are currently off.
      // Sequential (not Promise.all) so the preference writes don't race.
      const smsEventsToEnable: SmsNotificationEvent[] = enableSmsOnSave
        ? smsEvents.filter((event) => !enabledSmsPreferences?.[`${event}Sms`])
        : [];

      for (const event of smsEventsToEnable) {
        // eslint-disable-next-line no-await-in-loop -- serialize preference writes
        await enableSmsPreferenceMutation({ channel: 'sms', event, enabled: true });
      }

      await updateUserMutation({
        mobilePhone: `+1${data.mobilePhone}`,
      });

      await queryClient.invalidateQueries({ queryKey: userQueryKey });

      clearUserAttributesCache();

      // Mark resolved so the unmount guard doesn't also fire an implicit
      // dismissal when the host tears the form down.
      resolvedRef.current = true;

      reset();

      segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_SAVED, {
        source,
        editType: 'mobile_number',
      });

      // Canonical per-surface opt-in signal, fired only when this save newly
      // turned SMS notifications on (distinct from a number-only edit or
      // re-saving with SMS already enabled).
      if (smsEventsToEnable.length > 0) {
        trackSmsNotificationsEnabled({ source, smsEvents: smsEventsToEnable, hadPhoneNumber });
      }

      if (showSuccessToast) {
        toast.showSuccess({
          title: enableSmsOnSave ? 'Text notifications on' : 'Mobile number added',
        });
      }

      onComplete?.();
    } catch (error) {
      console.error('MobileNumberSetupForm save ERROR:', error);

      trackMobileNumberSetupFailed({ source, error: String(error) });

      toast.showError({
        title: 'Unable to save mobile number',
        apiError: { error },
      });
    }
  });

  const handleSkip = useCallback(() => {
    // Mark resolved so the unmount guard doesn't also fire an implicit
    // dismissal — this is the explicit Skip path.
    resolvedRef.current = true;

    trackMobileNumberSetupDismissed({ source, dismissMethod: 'skip', isDirty });

    reset();

    onSkip?.();
  }, [source, isDirty, reset, onSkip]);

  return (
    <VStack space="md">
      {showHeader ? (
        <VStack space="xs">
          <Heading size="lg">{SMS_NOTIFICATION_COPY[source].heading}</Heading>
          <Text size="sm" className="text-typography-700">
            {SMS_NOTIFICATION_COPY[source].body}
          </Text>
        </VStack>
      ) : null}

      <FormControl isInvalid={Boolean(isSubmitted && errors.mobilePhone)}>
        <FormControlLabel>
          <FormControlLabelText className="text-sm text-typography-700">
            Mobile number
          </FormControlLabelText>
        </FormControlLabel>

        {/* Validation is submit-gated: the error is withheld until the user
            presses the primary button with an invalid field (`isSubmitted`),
            then re-validates live as they fix it, so it never nags mid-entry. */}
        <Input>
          <InputField
            autoFocus={autoFocus}
            editable={!isSubmitting}
            keyboardType="phone-pad"
            {...mobileNumber.inputProps}
            onBlur={mobileNumber.controller.field.onBlur}
          />
        </Input>

        {isSubmitted && errors.mobilePhone && (
          <FormControlError accessibilityLiveRegion="polite">
            <FormControlErrorIcon as={AlertCircleIcon} />
            <FormControlErrorText>{errors.mobilePhone.message}</FormControlErrorText>
          </FormControlError>
        )}
      </FormControl>

      <Text size="xs" className="text-typography-700">
        {SMS_NOTIFICATION_REASSURANCE}
      </Text>

      <HStack space="md" className="w-full items-center justify-end">
        <Button
          onPress={handleSkip}
          action="secondary"
          variant="link"
          isDisabled={isSubmitting}
          className="px-2">
          <ButtonText>Not now</ButtonText>
        </Button>

        <Button
          onPress={onSubmit}
          action="primary"
          variant="solid"
          isDisabled={(requireDirty && !isDirty) || isSubmitting}>
          {isSubmitting ? (
            <ButtonSpinner color="white" />
          ) : (
            <ButtonText>{effectiveSaveLabel}</ButtonText>
          )}
        </Button>
      </HStack>

      {onOptOut ? (
        <Button
          onPress={onOptOut}
          action="secondary"
          variant="link"
          size="sm"
          className="self-center"
          isDisabled={isSubmitting}>
          <ButtonText className="text-sm text-typography-500 underline">
            I don&apos;t want text notifications
          </ButtonText>
        </Button>
      ) : null}
    </VStack>
  );
}
